import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMaintenanceStatus } from 'src/services/maintenance';
import {
  MaintenanceSnapshot,
  readMaintenanceSnapshot,
  writeMaintenanceSnapshot,
} from 'src/services/maintenance.storage';
import { MaintenanceBannerState, MaintenanceContext, deriveMaintenanceState } from 'src/services/maintenanceState';

export const MAINTENANCE_QUERY_KEY = ['maintenance'] as const;
/** Polling while nothing is going on. */
export const MAINTENANCE_POLL_MS = 5 * 60 * 1000;
/** Polling while maintenance is running or the backend is down, so "completed" shows up quickly. */
export const MAINTENANCE_POLL_ACTIVE_MS = 60 * 1000;
/** Re-render cadence for the purely time-based transitions (planned -> running -> done). */
export const MAINTENANCE_TICK_MS = 30 * 1000;
export const MAINTENANCE_STALE_MS = 30 * 1000;

/**
 * Banner state for the login screen (`context: 'login'`) and the portal.
 *
 * Polls the public maintenance endpoint, keeps the last answer in localStorage
 * and derives the state from the stored window, the server-corrected clock and
 * whether the backend answered in this session. Both mount points share the
 * query key, so only one request is in flight at a time.
 */
export const useMaintenanceBanner = (
  context: MaintenanceContext,
): { state: MaintenanceBannerState; dismiss: () => void } => {
  const [snapshot, setSnapshot] = useState<MaintenanceSnapshot>(readMaintenanceSnapshot);
  const [active, setActive] = useState(false);
  const [, setTick] = useState(0);

  const query = useQuery({
    queryKey: MAINTENANCE_QUERY_KEY,
    queryFn: ({ signal }) => fetchMaintenanceStatus(signal),
    retry: 1,
    retryDelay: 1000,
    staleTime: MAINTENANCE_STALE_MS,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    refetchInterval: active ? MAINTENANCE_POLL_ACTIVE_MS : MAINTENANCE_POLL_MS,
  });

  // Every successful answer becomes the new snapshot, "no window" included, so
  // a withdrawn announcement clears the cache. Dismiss markers survive. The
  // clock offset is taken against the moment the answer arrived
  // (`dataUpdatedAt`), not against "now": a second mount (the portal after
  // sign-in) receives the cached answer minutes later.
  const { data, dataUpdatedAt } = query;
  useEffect(() => {
    if (!data) return;
    setSnapshot(previous => ({
      window: data.window,
      fetchedAt: new Date(dataUpdatedAt).toISOString(),
      clockOffsetMs: data.serverTime ? Date.parse(data.serverTime) - dataUpdatedAt : 0,
      dismissed: previous.dismissed,
    }));
  }, [data, dataUpdatedAt]);

  useEffect(() => {
    writeMaintenanceSnapshot(snapshot);
  }, [snapshot]);

  useEffect(() => {
    const timer = setInterval(() => setTick(value => value + 1), MAINTENANCE_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const now = Date.now() + snapshot.clockOffsetMs;
  const end = snapshot.window ? Date.parse(snapshot.window.end) : null;

  // `null` until this session got an answer or a definite failure: only a
  // confirmed answer may decide between "completed" and "still in progress".
  // After the window end that answer must also have arrived after the end;
  // a poll from a minute before it says nothing about whether the backend
  // survived the deployment.
  const answeredAfterEnd = end === null || now < end || dataUpdatedAt + snapshot.clockOffsetMs >= end;
  let reachable: boolean | null = null;
  if (query.isError) reachable = false;
  else if (query.data !== undefined && answeredAfterEnd) reachable = true;

  const needsConfirmation = query.data !== undefined && !query.isError && !answeredAfterEnd;
  const { isFetching, refetch } = query;
  useEffect(() => {
    if (needsConfirmation && !isFetching) refetch();
  }, [needsConfirmation, isFetching]);

  const state = deriveMaintenanceState({
    window: snapshot.window,
    now,
    reachable,
    dismissed: snapshot.dismissed,
    context,
  });

  useEffect(() => {
    setActive(state.kind === 'running' || state.kind === 'unreachable');
  }, [state.kind]);

  const dismiss = useCallback(() => {
    if (state.kind !== 'planned' && state.kind !== 'done') return;
    const { kind, window: announced } = state;
    setSnapshot(previous => ({
      ...previous,
      dismissed:
        kind === 'planned'
          ? { ...previous.dismissed, planned: announced.start }
          : { ...previous.dismissed, done: announced.start },
    }));
  }, [state]);

  return { state, dismiss };
};
